import { useMatch } from '@tanstack/react-router';
import { useAtom, useSetAtom } from 'jotai';
import { Sparkle, WandSparkles } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { env } from '@/constants/env';
import { roomModeSettingsAtom } from '@/features/image-rendering/atoms/settings';
import { ROOM_MODE_OPTIONS } from '@/features/image-rendering/constants/room-mode';
import { cn } from '@/lib/utils';
import { createTempId } from '@/utils/tempId';

import { AiRenderingTab } from '../ai-rendering/components/ai-rendering-tab';
import { MagicEditTab } from '../magic-edit/components/magic-edit-tab';
import { renderingsActiveToolAtom } from '../shared/atoms/active-tool';
import {
  renderingsSelectedImageAtom,
  renderingsSelectedPreviewImageAtom,
  renderingsUploadedImagesAtom,
} from '../shared/atoms/image-files';
import { renderingsEstateIdAtom, renderingsProjectIdAtom } from '../shared/atoms/relation';
import { SourceImageCard } from '../shared/components/source-image-card';
import { SourceObjectCard } from '../shared/components/source-object-card';

type Props = {
  onMagicEditCreate?: () => void;
  isMagicEditCreating?: boolean;
  isMobileView?: boolean;
  onContinue?: () => void;
  onAiRenderingCreate?: () => void;
  isAiRenderingCreating?: boolean;
  canGenerateAiRendering?: boolean;
};

type RenderingsPrefill = {
  imageId?: string;
  imageUrl?: string;
  fileType?: string;
  estateId?: string;
  projectId?: string;
};

const createImageFromId = (imageId: string) => ({
  id: createTempId(),
  fileType: 'image/jpeg',
  file: null,
  originalImage: `${env.API_URL}/images/${imageId}`,
  originalImageId: imageId,
});

const createImageFromPrefill = (prefill: RenderingsPrefill) => {
  if (prefill.imageId) return createImageFromId(prefill.imageId);
  if (!prefill.imageUrl) return null;

  return {
    id: createTempId(),
    fileType: prefill.fileType ?? 'image/jpeg',
    file: null,
    originalImage: prefill.imageUrl,
  };
};

export const RenderingsSidebar = ({
  onMagicEditCreate,
  isMagicEditCreating,
  isMobileView,
  onContinue,
  onAiRenderingCreate,
  isAiRenderingCreating,
  canGenerateAiRendering,
}: Props) => {
  const { t } = useTranslation();
  const [activeTool, setActiveTool] = useAtom(renderingsActiveToolAtom);

  const renderingsMatch = useMatch({
    from: '/_authenticated/_sidebar-layout/image-rendering',
    shouldThrow: false,
  });

  const search = (renderingsMatch?.search ?? {}) as Partial<{
    imageId: string;
    mediaId: string;
    estateId: string;
    projectId: string;
    source: 'editor';
    tool: 'ai-rendering' | 'magic-edit';
  }>;

  const imageIdFromParams = search.imageId;
  const mediaIdFromParams = search.mediaId;
  const estateIdFromParams = search.estateId;
  const projectIdFromParams = search.projectId;
  const sourceFromParams = search.source;
  const toolFromParams = search.tool;

  const imageIdToUse = imageIdFromParams ?? mediaIdFromParams;

  const setUploadedImages = useSetAtom(renderingsUploadedImagesAtom);
  const setSelectedImage = useSetAtom(renderingsSelectedImageAtom);
  const setSelectedPreviewImage = useSetAtom(renderingsSelectedPreviewImageAtom);
  const setEstateId = useSetAtom(renderingsEstateIdAtom);
  const setProjectId = useSetAtom(renderingsProjectIdAtom);
  const setRoomMode = useSetAtom(roomModeSettingsAtom);

  useEffect(() => {
    if (sourceFromParams === 'editor') {
      setActiveTool('ai-rendering');
      setRoomMode(ROOM_MODE_OPTIONS.PHOTOREALISTIC_FROM_3D.value);
      return;
    }

    setActiveTool(toolFromParams ?? 'ai-rendering');
  }, [setActiveTool, setRoomMode, sourceFromParams, toolFromParams]);

  useEffect(() => {
    const nextEstateId = estateIdFromParams ?? undefined;
    const nextProjectId = nextEstateId ? undefined : (projectIdFromParams ?? undefined);
    setEstateId(nextEstateId);
    setProjectId(nextProjectId);
  }, [estateIdFromParams, projectIdFromParams, setEstateId, setProjectId]);

  useEffect(() => {
    if (!imageIdToUse) return;

    const imageFromParams = createImageFromId(imageIdToUse);

    setSelectedImage(imageFromParams);
    setSelectedPreviewImage(imageFromParams);
    setUploadedImages((prev) => {
      if (prev.findIndex((img) => img.originalImageId === imageIdToUse) > -1) return prev;
      return [...prev, imageFromParams];
    });
  }, [imageIdToUse, setSelectedImage, setSelectedPreviewImage, setUploadedImages]);

  useEffect(() => {
    const rawPrefill = sessionStorage.getItem('renderings-prefill');
    if (!rawPrefill) return;

    const timeoutId = window.setTimeout(() => {
      try {
        const prefill = JSON.parse(rawPrefill) as RenderingsPrefill;
        const nextEstateId = prefill.estateId ?? undefined;
        const nextProjectId = nextEstateId ? undefined : (prefill.projectId ?? undefined);

        setEstateId(nextEstateId);
        setProjectId(nextProjectId);

        const imageFromStorage = createImageFromPrefill(prefill);

        if (imageFromStorage) {
          setSelectedImage(imageFromStorage);
          setSelectedPreviewImage(imageFromStorage);
          setUploadedImages((prev) => {
            if (
              prev.findIndex((img) => img.originalImage === imageFromStorage.originalImage) > -1
            ) {
              return prev;
            }
            return [...prev, imageFromStorage];
          });
        }
      } finally {
        sessionStorage.removeItem('renderings-prefill');
      }
    });

    return () => window.clearTimeout(timeoutId);
  }, [setEstateId, setProjectId, setSelectedImage, setSelectedPreviewImage, setUploadedImages]);

  void sourceFromParams;

  useEffect(() => {
    return () => {
      setUploadedImages([]);
      setSelectedImage(undefined);
      setSelectedPreviewImage(undefined);
      setEstateId(undefined);
      setProjectId(undefined);
      setActiveTool('ai-rendering');
    };
  }, [
    setActiveTool,
    setEstateId,
    setProjectId,
    setSelectedImage,
    setSelectedPreviewImage,
    setUploadedImages,
  ]);

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <Tabs
        value={activeTool}
        onValueChange={(value) => setActiveTool(value as 'ai-rendering' | 'magic-edit')}
        className={cn('flex min-h-0 flex-1 flex-col', isMobileView && 'w-full')}
      >
        <TabsList
          className={cn(
            'grid h-8 grid-cols-2 overflow-hidden rounded-xl border-[0.5px] border-slate-200 bg-white p-0',
            isMobileView ? 'w-full max-w-full' : 'w-fit'
          )}
        >
          <TabsTrigger
            value="ai-rendering"
            className={cn(
              'h-8 justify-center gap-1.5 rounded-none border-r border-slate-200 px-0 py-2 text-center text-[12px] font-medium leading-4 shadow-none transition-colors',
              isMobileView ? 'w-full min-w-0' : 'w-[143px]',
              'text-slate-500 data-[state=active]:border-[#07b4c0] data-[state=active]:bg-[#07b4c0] data-[state=active]:text-white'
            )}
          >
            <Sparkle aria-hidden className="size-4" />
            <span>{t('ai-rendering')}</span>
          </TabsTrigger>

          <TabsTrigger
            value="magic-edit"
            className={cn(
              'h-8 justify-center gap-1.5 rounded-none px-0 py-2 text-center text-[12px] font-medium leading-4 shadow-none transition-colors',
              isMobileView ? 'w-full min-w-0' : 'w-[143px]',
              'text-slate-500 data-[state=active]:border-[#07b4c0] data-[state=active]:bg-[#07b4c0] data-[state=active]:text-white'
            )}
          >
            <WandSparkles aria-hidden className="size-4" />
            <span>{t('magic-edit')}</span>
          </TabsTrigger>
        </TabsList>

        <div className="-mr-2 mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overflow-x-hidden bg-white [scrollbar-color:#cbd5e1_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5">
          <div className="shrink-0">
            <SourceImageCard />
          </div>
          <div className="shrink-0">
            <SourceObjectCard />
          </div>
          <TabsContent
            value="ai-rendering"
            className="m-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
          >
            <AiRenderingTab
              isMobileView={isMobileView}
              onContinue={onContinue}
              onCreate={onAiRenderingCreate}
              isCreating={isAiRenderingCreating}
              canGenerate={canGenerateAiRendering}
            />
          </TabsContent>

          <TabsContent
            value="magic-edit"
            className="m-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
          >
            <MagicEditTab
              onCreate={() => onMagicEditCreate?.()}
              isCreating={isMagicEditCreating}
              isMobileView={isMobileView}
              onContinue={onContinue}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};
